import React, {useContext, useEffect, useState} from 'react';
import {Box, Button, Card, CardContent, Grid, useTheme} from "@mui/material";
import Loading from "../../common/components/Loading";
import SectionHeader from "../../../../util/component/SectionHeader";
import {FormAutocomplete, FormDatePicker} from "../../common/form-fields";
import {SelectElementModelBE, SelectElementModelFE} from "../../api/types/generic";
import dayjs from "dayjs";
import {FormProvider, useForm} from "react-hook-form";
import {ExportFormModel, initialExportForm} from "./Export";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import * as yup from "yup";
import {useTranslation} from "react-i18next";
import {useProductCodeRepository} from "../../api/repositories/ProductCodeRepository";
import {ProductCodeEntityResponse} from "../../api/types/productCode";
import {useExportRepository} from "../../api/repositories/ExportRepository";
import {PagedResponse} from "../../api/types/pagination";
import DealerAndTenant, {DealerTenantEntity} from "../../common/components/DealerAndTenant";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";

const ExportPricing = () => {

    // Use Theme
    const theme = useTheme();

    // Use Translation
    const {t} = useTranslation(["tranlsation", "pricing"]);

    // Use Context
    const {
        dispatchGlobalData,
        globalData: {user},
    } = useContext(GlobalContext);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    // Use State
    const [, setProductCodes] = useState<ProductCodeEntityResponse[]>([]);
    const [productCodesMapped, setProductCodesMapped] = useState<SelectElementModelFE[]>([]);
    const [dealerOptions, setDealerOptions] = useState([])

    // Use Repository
    const {getCustomProductCode} = useProductCodeRepository();
    const {getExport} = useExportRepository();

    const validationSchema = yup.object({
        productCode: yup
            .object()
            .shape({
                code: yup.string(),
                label: yup.string()
            })
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        from: yup.string().required(t("translation:validation.fieldMandatory")),
        to: yup.string().required(t("translation:validation.fieldMandatory")),
        dealer: yup
            .object()
            .shape({
                code: yup.string(),
                label: yup.string()
            })
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        legalEntity: yup
            .object()
            .shape({
                code: yup.string(),
                label: yup.string()
            })
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
    });

    // Use Form
    const form = useForm<ExportFormModel>({
        defaultValues: initialExportForm,
        resolver: yupResolver(
            validationSchema,
            {},
            {
                rawValues: true
            }
        ),
        shouldUnregister: true
    });

    const handleChangeProductCode = async (value: SelectElementModelFE) => {
        form.setValue("productCode", value)
        form.setValue("from", null)
        form.setValue("to", null)
    }

    const handleButtonClick = async () => {
        const watchAllForm = form.getValues();
        const productCode = watchAllForm.productCode?.code as string;
        const from = dayjs(watchAllForm.from).format("YYYY-MM-DD")
        const to = dayjs(watchAllForm.to).format("YYYY-MM-DD")

        await getExport(productCode, from, to)
    }

    const updateDealerSelection = async (value: SelectElementModelFE) => {
        form.setValue(`legalEntity`, value);
        form.setValue(`dealer`, null);
        const filteredOptionsDealer = user.dealers?.filter(
            (el: DealerTenantEntity) => el.legalEntities?.some((el: { name: string | number; }) => el.name === value?.code)
        );
        const filteredList = filteredOptionsDealer.map(
            (el: DealerTenantEntity) => ({
                code: el.name,
                label: t('translation:dealers.'.concat(el.name)),
            })
        );
        setDealerOptions(filteredList);
    };

    const handleChangeDealer = async (value: SelectElementModelFE) => {
        form.setValue(`dealer`, value);
        const product: PagedResponse<ProductCodeEntityResponse> = await getCustomProductCode(value.code as string, (form.watch("legalEntity") as SelectElementModelFE).code as string);
        setProductCodes(product.content)
        const productMapped: SelectElementModelFE[] = product.content.map(el => ({
            code: el.id,
            label: el.id + " - " + el.name
        }))
        setProductCodesMapped(productMapped)
    };

    return (
        <Box>
            <SectionHeader title="Export"/>
            <Grid container spacing={2} alignItems="start">
                <Grid item xs={12} sm={12} md={12}>
                    <Card>
                        <CardContent>
                            <FormProvider {...form}>
                                <Grid container spacing={2} py={2}>
                                    {/*LEGAL ENTITY*/}
                                    <Grid item xs={12} md={3}>
                                        <FormAutocomplete
                                            {...form.register("legalEntity")}
                                            label={`${t("pricing:dealer.tenant")}`}
                                            //getOptionLabel={(option: any) => t('translation:legalEntities.'.concat(option.label))}
                                            options={optionsFiltered || []}
                                            handleChange={updateDealerSelection}
                                            isOptionEqualToValue={(
                                                option: SelectElementModelFE,
                                                value: SelectElementModelFE
                                            ) => option.code === value.code}
                                        />
                                    </Grid>
                                    {/*DEALER*/}
                                    <Grid item xs={12} md={3}>
                                        <FormAutocomplete
                                            {...form.register(`dealer`)}
                                            label={`${t("pricing:dealer.dealer")}`}
                                            options={dealerOptions || []}
                                            readOnly={Boolean(!form.watch("legalEntity"))}
                                            //getOptionLabel={(option: any) => t('translation:dealers.'.concat(option.label))}
                                            isOptionEqualToValue={(
                                                option: SelectElementModelFE,
                                                value: SelectElementModelFE
                                            ) => option.code === value.code}
                                            handleChange={handleChangeDealer}
                                        />
                                    </Grid>
                                    {/* PRODUCT CODE */}
                                    <Grid item xs={12} md={3}>
                                        <FormAutocomplete
                                            {...form.register("productCode")}
                                            label={`${t("pricing:rateProductCodeLocationGroup.form.labels.productCode")}*`}
                                            options={productCodesMapped || []}
                                            isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                                            handleChange={handleChangeProductCode}
                                        />
                                    </Grid>
                                    {/* PICKUP DATE FROM */}
                                    <Grid item xs={12} md={3}>
                                        <FormDatePicker
                                            {...form.register("from")}
                                            label={`${t("pricing:ancillary.form.labels.pickupDateFrom")}*`}
                                            readOnly={!form.watch("productCode")}
                                            handleChange={(value: string) => {
                                                if (value) {
                                                    form.setValue("from", value)
                                                    form.setValue("to", null)
                                                } else {
                                                    form.setValue("from", value)
                                                    form.setValue("to", null)
                                                }
                                            }}
                                        />
                                    </Grid>
                                    {/* PICKUP DATE TO */}
                                    <Grid item xs={12} md={3}>
                                        <FormDatePicker
                                            {...form.register("to")}
                                            label={`${t("pricing:ancillary.form.labels.pickupDateTo")}*`}
                                            readOnly={!form.watch("from")}
                                            minDate={form.watch("from")}
                                        />
                                    </Grid>
                                    <Grid
                                        container
                                        item
                                        xs={12}
                                        justifyContent="flex-end"
                                        columnGap={2}
                                    >
                                        <Grid>
                                            <Button
                                                color={"accent"}
                                                type={"button"}
                                                onClick={handleButtonClick}
                                                sx={{
                                                    border: `1px solid ${theme.palette.accent.main}`
                                                }}
                                                disabled={!form.watch("to")}
                                            >
                                                Export
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </FormProvider>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            <Loading/>
        </Box>
    )
};
export default ExportPricing;